/**
 * LocationStep - Select state and optionally city
 */

import React from 'react';
import { Stack, Text, Select, TextInput, Group, Paper, Badge } from '@mantine/core';
import { IconMapPin } from '@tabler/icons-react';
import { US_STATES } from '../hooks/useComposition';

export interface LocationStepProps {
  state?: string;
  city?: string;
  onStateChange: (state: string) => void;
  onCityChange: (city: string) => void;
}

// States with specific licensing workflows
const STATES_WITH_SPECIAL_WORKFLOWS = ['MN', 'WI', 'CA', 'TX', 'FL', 'NY'];

export function LocationStep({ state, city, onStateChange, onCityChange }: LocationStepProps) {
  const hasSpecialWorkflows = state && STATES_WITH_SPECIAL_WORKFLOWS.includes(state);

  return (
    <Stack gap="lg" py="md">
      <Text size="lg" fw={500}>
        Where will your business operate?
      </Text>

      <Paper p="md" withBorder>
        <Stack gap="md">
          <Select
            label="State"
            placeholder="Select your state"
            data={US_STATES}
            value={state}
            onChange={(value) => onStateChange(value || '')}
            searchable
            required
            leftSection={<IconMapPin size={16} />}
          />

          {hasSpecialWorkflows && (
            <Group gap="xs">
              <Badge size="sm" color="blue" variant="light">
                State-specific workflows available
              </Badge>
            </Group>
          )}

          <TextInput
            label="City"
            placeholder="Enter your city (optional)"
            value={city}
            onChange={(e) => onCityChange(e.currentTarget.value)}
            description="Some cities have additional licensing requirements"
          />
        </Stack>
      </Paper>

      <Text size="sm" c="dimmed">
        Your state determines which licenses, permits, and certifications are required.
        We&apos;ll customize your workflow based on state-specific requirements.
      </Text>
    </Stack>
  );
}

export default LocationStep;
